// Web sitesinin sponsor alanları — yönetimdeki "Sponsorlar" ekranından gelir.
//
//   SiteSponsorRails   geniş ekranlarda içeriğin sağında ve solunda sabit
//                      duran dikey bantlar (web_left / web_right)
//   SiteSponsorBanner  sayfa içi banner (web_banner; mobilde web_mobile)
//   SiteSponsorStrip   alt bilgideki "Sponsorlarımız" logo şeridi (web_footer)
//
// Liste herkese açık /public/sponsors ucundan okunur ve beş dakika önbellekte
// tutulur. Sayfa bir ile aitse (/sehir/:id/...) o ilin ve tüm illerin
// sponsorları gösterilir. Sponsor yoksa hiçbir şey çizilmez.
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchSiteSponsors } from "../services/sponsorsApi";
import "./SiteSponsors.css";

const cityFromPath = (pathname = "") => {
  const match = /^\/sehir\/(\d+)/.exec(pathname);
  return match ? match[1] : null;
};

export function useSiteSponsors(cityIdProp) {
  const location = useLocation();
  const cityId = cityIdProp ?? cityFromPath(location.pathname);
  const [list, setList] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchSiteSponsors(cityId)
      .then((items) => { if (alive) setList(items); })
      .catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [cityId]);

  return list;
}

const has = (sponsor, key) => Array.isArray(sponsor?.placements) && sponsor.placements.includes(key);

/** Sponsor bağlantısı varsa tıklanabilir sarmalayıcı. */
const SponsorLink = ({ sponsor, className, children }) =>
  sponsor.linkUrl ? (
    <a className={className} href={sponsor.linkUrl} target="_blank" rel="sponsored noopener noreferrer" title={sponsor.name}>
      {children}
    </a>
  ) : (
    <div className={className} title={sponsor.name}>{children}</div>
  );

/** Aynı alanda birden fazla sponsor varsa belirli aralıkla döner. */
function useRotation(count, ms = 9000) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    if (count < 2) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), ms);
    return () => window.clearInterval(id);
  }, [count, ms]);
  return count ? index % count : 0;
}

export function SiteSponsorRails() {
  const sponsors = useSiteSponsors();
  const left = useMemo(() => sponsors.filter((s) => has(s, "web_left") && s.images?.web_left), [sponsors]);
  const right = useMemo(() => sponsors.filter((s) => has(s, "web_right") && s.images?.web_right), [sponsors]);
  const li = useRotation(left.length);
  const ri = useRotation(right.length);
  if (!left.length && !right.length) return null;
  const rail = (side, sponsor) => (
    <aside className={`site-sponsor-rail site-sponsor-rail--${side}`} aria-label="Sponsor">
      <SponsorLink sponsor={sponsor} className="site-sponsor-rail__link">
        <img key={sponsor.id} src={sponsor.images[`web_${side}`]} alt={sponsor.name} loading="lazy" />
      </SponsorLink>
      <small>Sponsor</small>
    </aside>
  );
  return (
    <>
      {left.length ? rail("left", left[li]) : null}
      {right.length ? rail("right", right[ri]) : null}
    </>
  );
}

export function SiteSponsorBanner({ cityId, className = "" }) {
  const sponsors = useSiteSponsors(cityId);
  const banners = useMemo(() => sponsors.filter((s) => has(s, "web_banner") && (s.images?.web_banner || s.images?.web_mobile)), [sponsors]);
  const index = useRotation(banners.length);
  if (!banners.length) return null;
  const sponsor = banners[index];
  const desktop = sponsor.images.web_banner || sponsor.images.web_mobile;
  const mobile = sponsor.images.web_mobile || sponsor.images.web_banner;
  return (
    <section className={`site-sponsor-banner ${className}`} aria-label="Sponsor">
      <SponsorLink sponsor={sponsor} className="site-sponsor-banner__link">
        <picture key={sponsor.id}>
          <source media="(max-width: 640px)" srcSet={mobile} />
          <img src={desktop} alt={sponsor.name} loading="lazy" />
        </picture>
      </SponsorLink>
      <small className="site-sponsor-banner__tag">Sponsor</small>
    </section>
  );
}

export function SiteSponsorStrip() {
  const sponsors = useSiteSponsors();
  const logos = sponsors.filter((s) => has(s, "web_footer") && s.images?.logo);
  if (!logos.length) return null;
  return (
    <section className="site-sponsor-strip" aria-label="Sponsorlarımız">
      <span className="site-sponsor-strip__title">Sponsorlarımız</span>
      <div className="site-sponsor-strip__logos">
        {logos.map((sponsor) => (
          <SponsorLink key={sponsor.id} sponsor={sponsor} className="site-sponsor-strip__item">
            <img src={sponsor.images.logo} alt={sponsor.name} loading="lazy" />
          </SponsorLink>
        ))}
      </div>
    </section>
  );
}
